/**
 * NotificationBell — dashboard header bell with an unread badge and a dropdown
 * inbox. Backed by `/v1/notifications`.
 *
 * Click behaviour:
 *   - If the notification carries a linked entity (invitationId, etc.) the user
 *     is navigated to that entity's page so they see the full context.
 *   - Otherwise a detail modal opens showing the untruncated message.
 *   - Either way the notification is marked read.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icons } from './Icons';
import {
  getNotifications,
  getUnreadNotificationCount,
  markNotificationRead,
  markAllNotificationsRead,
  deleteNotification,
  type ApiNotification,
} from '@/src/infrastructure/lib/api';

/** Compact relative-time label, e.g. "3m", "2h", "5d". */
function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const min = Math.floor(diff / 60_000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h`;
  return `${Math.floor(hr / 24)}d`;
}

type NotificationTarget =
  | { kind: 'route'; path: string }
  | { kind: 'external'; href: string }
  | { kind: 'modal' };

/**
 * Pick where clicking a notification should take the user. The resolver leans
 * on `metadata` IDs first (most specific), then falls back to category-level
 * destinations, then a modal that just shows the full text.
 */
function resolveTarget(n: ApiNotification): NotificationTarget {
  const meta = (n.metadata ?? {}) as Record<string, unknown>;
  const invitationId = typeof meta.invitationId === 'string' ? meta.invitationId : null;
  const feedbackId = typeof meta.feedbackId === 'string' ? meta.feedbackId : null;
  const ticketId = typeof meta.ticketId === 'string' ? meta.ticketId : null;
  const ctaUrl = typeof meta.ctaUrl === 'string' ? meta.ctaUrl : null;

  if (feedbackId) return { kind: 'route', path: `/dashboard/feedback/${feedbackId}` };
  if (ticketId) return { kind: 'route', path: `/dashboard/help/tickets/${ticketId}` };
  if (invitationId) {
    return { kind: 'route', path: `/dashboard/invitations?highlight=${invitationId}` };
  }
  if (n.category === 'MARKETING' && ctaUrl) return { kind: 'external', href: ctaUrl };
  // Security, credit, and other category notifications without a specific
  // resource navigate to the Notification Center so users can see full detail
  // in context — never to Settings.
  if (['SECURITY', 'CREDIT', 'BILLING', 'ACCOUNT'].includes(n.category)) {
    return { kind: 'route', path: '/dashboard/notifications' };
  }
  return { kind: 'modal' };
}

export function NotificationBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [items, setItems] = useState<ApiNotification[]>([]);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState<ApiNotification | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  const refreshCount = useCallback(() => {
    getUnreadNotificationCount()
      .then((r) => setUnread(r.unreadCount))
      .catch(() => undefined); // badge is best-effort; never surface an error
  }, []);

  // Poll the unread count: once on mount, then every 60s.
  useEffect(() => {
    refreshCount();
    const id = window.setInterval(refreshCount, 60_000);
    return () => window.clearInterval(id);
  }, [refreshCount]);

  // Close the dropdown on an outside click.
  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  // Load the list whenever the dropdown opens.
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    getNotifications({ limit: 10 })
      .then((page) => {
        setItems(page.items);
        setUnread(page.unreadCount);
      })
      .catch(() => undefined)
      .finally(() => setLoading(false));
  }, [open]);

  async function markReadLocal(n: ApiNotification) {
    if (n.isRead) return;
    setItems((list) => list.map((x) => (x.id === n.id ? { ...x, isRead: true } : x)));
    setUnread((c) => Math.max(0, c - 1));
    await markNotificationRead(n.id).catch(() => undefined);
  }

  async function handleOpen(n: ApiNotification) {
    await markReadLocal(n);
    const target = resolveTarget(n);
    if (target.kind === 'route') {
      setOpen(false);
      navigate(target.path);
    } else if (target.kind === 'external') {
      setOpen(false);
      window.open(target.href, '_blank', 'noopener,noreferrer');
    } else {
      setDetail({ ...n, isRead: true });
      setOpen(false);
    }
  }

  async function handleMarkAll() {
    setItems((list) => list.map((x) => ({ ...x, isRead: true })));
    setUnread(0);
    await markAllNotificationsRead().catch(() => undefined);
  }

  async function handleDelete(id: string, e: React.MouseEvent) {
    e.stopPropagation(); // don't also trigger mark-read
    const removed = items.find((x) => x.id === id);
    setItems((list) => list.filter((x) => x.id !== id));
    if (removed && !removed.isRead) setUnread((c) => Math.max(0, c - 1));
    await deleteNotification(id).catch(() => undefined);
  }

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        aria-label={`Notifications${unread ? ` (${unread} unread)` : ''}`}
        className="w-10 h-10 flex items-center justify-center hover:bg-surface-low rounded-xl transition-colors text-on-surface-variant relative focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      >
        <Icons.Bell size={20} />
        {unread > 0 && (
          <span className="absolute top-1 right-1 min-w-[18px] h-[18px] px-1 flex items-center justify-center text-[10px] font-bold rounded-full bg-primary text-primary-foreground">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-[360px] max-w-[90vw] bg-surface-lowest rounded-2xl border border-outline-variant/40 shadow-lg z-50 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-outline-variant/30">
            <span className="text-sm font-bold text-on-surface">Notifications</span>
            {unread > 0 && (
              <button
                onClick={handleMarkAll}
                className="text-xs font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded"
              >
                Mark all read
              </button>
            )}
          </div>

          <div className="max-h-[400px] overflow-y-auto">
            {loading ? (
              <p className="px-4 py-8 text-center text-sm text-on-surface-variant">Loading…</p>
            ) : items.length === 0 ? (
              <p className="px-4 py-8 text-center text-sm text-on-surface-variant">
                You're all caught up.
              </p>
            ) : (
              items.map((n) => (
                <div
                  key={n.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => handleOpen(n)}
                  onKeyDown={(e) => {
                    // Ignore key presses that originated from nested interactive elements (like the delete button)
                    if (e.target !== e.currentTarget) return;
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleOpen(n);
                    }
                  }}
                  className={`group flex gap-3 px-4 py-3 cursor-pointer border-b border-outline-variant/20 last:border-0 transition-colors hover:bg-surface-low focus-visible:outline-none focus-visible:bg-surface-low focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-inset ${
                    n.isRead ? '' : 'bg-primary/[0.04]'
                  }`}
                >
                  <span
                    className={`mt-1.5 w-2 h-2 shrink-0 rounded-full ${
                      n.isRead ? 'bg-transparent' : 'bg-primary'
                    }`}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm font-semibold text-on-surface truncate">{n.title}</p>
                      <span className="text-[11px] text-on-surface-variant shrink-0">
                        {timeAgo(n.createdAt)}
                      </span>
                    </div>
                    <p className="text-xs text-on-surface-variant mt-0.5 line-clamp-2">
                      {n.message}
                    </p>
                  </div>
                  <button
                    onClick={(e) => handleDelete(n.id, e)}
                    aria-label="Delete notification"
                    className="opacity-0 group-hover:opacity-100 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-destructive rounded transition-opacity self-center text-on-surface-variant hover:text-destructive"
                  >
                    <Icons.Trash size={16} />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {detail && <NotificationDetailModal n={detail} onClose={() => setDetail(null)} />}
    </div>
  );
}

function NotificationDetailModal({
  n,
  onClose,
}: {
  n: ApiNotification;
  onClose: () => void;
}) {
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 px-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg bg-surface-lowest rounded-2xl border border-outline-variant/40 shadow-xl overflow-hidden"
      >
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-outline-variant/30">
          <div className="min-w-0">
            <p className="text-base font-bold text-on-surface">{n.title}</p>
            <p className="text-[11px] text-on-surface-variant mt-0.5">
              {new Date(n.createdAt).toLocaleString()} · {n.category}
            </p>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="text-on-surface-variant hover:text-on-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded"
          >
            <Icons.X size={18} />
          </button>
        </div>
        <div className="px-5 py-4 max-h-[60vh] overflow-y-auto">
          <p className="text-sm text-on-surface whitespace-pre-wrap">{n.message}</p>
        </div>
      </div>
    </div>
  );
}
